import React, { useReducer, useContext } from 'react';
import api from '../../utils/api';
import FriendContext from './friendContext';
import friendReducer from './friendReducer';
import AuthContext from '../auth/authContext';
import {
  GET_FRIENDS,
  GET_FRIEND_REQUESTS,
  SEARCH_USERS,
  CLEAR_SEARCH_RESULTS,
  SEND_FRIEND_REQUEST,
  ACCEPT_FRIEND_REQUEST,
  REJECT_FRIEND_REQUEST,
  REMOVE_FRIEND,
  FRIEND_ERROR,
  CLEAR_FRIENDS,
  CLEAR_ERRORS
} from '../types';

const FriendState = props => {
  const initialState = {
    friends: [],
    friendRequests: [],
    searchResults: [],
    error: null,
    loading: true
  };

  const [state, dispatch] = useReducer(friendReducer, initialState);
  const { user } = useContext(AuthContext);

  // Get all friends
  const getFriends = async () => {
    // Only allow authenticated users to fetch friends
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_FRIENDS,
        payload: []
      });
      return;
    }

    try {
      const res = await api.get('/api/friends');

      dispatch({
        type: GET_FRIENDS,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error fetching friends'
      });
    }
  };

  // Get friend requests
  const getFriendRequests = async () => {
    // Only allow authenticated users to fetch friend requests
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_FRIEND_REQUESTS,
        payload: []
      });
      return;
    }

    try {
      const res = await api.get('/api/friends/requests');

      dispatch({
        type: GET_FRIEND_REQUESTS,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error fetching friend requests'
      });
    }
  };

  // Search users
  const searchUsers = async (username) => {
    // Only allow authenticated users to search users
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: SEARCH_USERS,
        payload: []
      });
      return;
    }

    try {
      const res = await api.get(`/api/friends/search?username=${username}`);
      
      // Log search results to debug profile pictures
      console.log('User search results:', res.data);
      if (res.data && res.data.length > 0) {
        res.data.forEach(user => {
          console.log(`User ${user.username} profile picture:`, user.profilePicture);
        });
      }

      dispatch({
        type: SEARCH_USERS,
        payload: res.data
      });
    } catch (err) {
      console.error('Error in searchUsers:', err);
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error searching users'
      });
    }
  };

  // Send friend request
  const sendFriendRequest = async (username) => {
    // Only allow authenticated users to send friend requests
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to send friend requests';
      dispatch({
        type: FRIEND_ERROR,
        payload: error
      });
      throw new Error(error);
    }

    try {
      const res = await api.post('/api/friends/request', { username });

      dispatch({
        type: SEND_FRIEND_REQUEST
      });

      // Refresh friend requests after sending
      setTimeout(() => {
        getFriendRequests();
      }, 100);

      return res.data;
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error sending friend request'
      });
      throw err;
    }
  };

  // Accept friend request
  const acceptFriendRequest = async (userId) => {
    // Only allow authenticated users to accept friend requests
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: FRIEND_ERROR,
        payload: 'You must be logged in to accept friend requests'
      });
      return;
    }

    try {
      await api.put(`/api/friends/accept/${userId}`);

      dispatch({
        type: ACCEPT_FRIEND_REQUEST,
        payload: userId
      });

      // Refresh friend list and friend requests
      setTimeout(() => {
        getFriends();
        getFriendRequests();
      }, 100);
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error accepting friend request'
      });
    }
  };

  // Reject friend request
  const rejectFriendRequest = async (userId) => {
    // Only allow authenticated users to reject friend requests
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: FRIEND_ERROR,
        payload: 'You must be logged in to reject friend requests'
      });
      return;
    }

    try {
      await api.put(`/api/friends/reject/${userId}`);

      dispatch({
        type: REJECT_FRIEND_REQUEST,
        payload: userId
      });

      // Refresh friend requests after rejection
      setTimeout(() => {
        getFriendRequests();
      }, 100);
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error rejecting friend request'
      });
    }
  };

  // Remove friend
  const removeFriend = async (userId) => {
    // Only allow authenticated users to remove friends
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: FRIEND_ERROR,
        payload: 'You must be logged in to remove friends'
      });
      return;
    }

    try {
      await api.delete(`/api/friends/${userId}`);

      dispatch({
        type: REMOVE_FRIEND,
        payload: userId
      });

      // Refresh friends list after removal
      setTimeout(() => {
        getFriends();
      }, 100);
    } catch (err) {
      dispatch({
        type: FRIEND_ERROR,
        payload: err.response?.data?.message || 'Error removing friend'
      });
    }
  };

  // Clear friends
  const clearFriends = () => {
    dispatch({ type: CLEAR_FRIENDS });
  };

  // Clear search results
  const clearSearchResults = () => {
    dispatch({ type: CLEAR_SEARCH_RESULTS });
  };

  // Clear errors
  const clearErrors = () => {
    dispatch({ type: CLEAR_ERRORS });
  };

  return (
    <FriendContext.Provider
      value={{
        friends: state.friends,
        friendRequests: state.friendRequests,
        searchResults: state.searchResults,
        error: state.error,
        loading: state.loading,
        getFriends,
        getFriendRequests,
        searchUsers,
        clearSearchResults,
        sendFriendRequest,
        acceptFriendRequest,
        rejectFriendRequest,
        removeFriend,
        clearFriends,
        clearErrors
      }}
    >
      {props.children}
    </FriendContext.Provider>
  );
};

export default FriendState;